import React, { useContext, useEffect, useRef, useState } from 'react';
import { VideoCameraIcon } from '@heroicons/react/24/solid';
import ActivitySocialBar from './ActivitySocialBar';
import VideoThumbnail from './VideoThumbnail';
import CameraVideoGallery from './CameraVideoGallery';
import { formatMediaDuration } from './MediaGalleryStage';
import { useModal } from '../../services/ModalService';
import './Media.css';
import './CameraVideoMedia.css';
import { SharedCameraStreamsContext } from '../camera/SharedCameraStreams';
import { createOwnerCameraArchiveApi } from '../../services/ownerCameraArchiveApi';
import { API_BASE_URL } from '../../config';

// A broadcast rendered as its canonical Media activity (a daily Live parent or a
// legacy session) whose videos are ordinary Media video attachments: the same
// gallery tile and VideoThumbnail poster, opening the same shared gallery
// (MediaGalleryStage) uploads use. Lifecycle
// is shown only as the server reports it; the Live badge and Watch live exist
// only while the server says live. Bytes come only from the typed camera replay
// route, and owner delete is the camera tombstone (never the upload-file path).
const AUDIENCE_LABELS = { public: 'Public', friends: 'Friends', dojo: 'Dojo', dojo_chief: 'Dojo Chief' };
const UNAVAILABLE = {
  broadcast_failed: 'This broadcast failed. Video unavailable.',
  no_recording: 'No video was saved for this broadcast.',
  recording_failed: 'The video could not be saved.',
  recording_partial: 'Only part of this video was saved. Playback is unavailable.',
  unknown: 'Video unavailable.',
};
const DELETION = { sending: 'Deleting…', pending: 'Video deleted. Erasure pending.', complete: 'Video deleted.', unknown: 'Deletion outcome unknown. Retry.' };

function CameraSocialBar({ activity, backendService, isOwner, social }) {
  const identity = backendService.authenticatedIdentity, ownerId = backendService.positionOwner;
  const key = `${identity}|${ownerId}|${activity._id}`;
  const [summary, setSummary] = useState(null);
  useEffect(() => {
    if (social || !identity || !ownerId || !backendService.client?.post) return undefined;
    let active = true; const abort = new AbortController();
    Promise.resolve().then(() => active && backendService.authenticatedIdentity === identity && backendService.positionOwner === ownerId
      ? backendService.client.post(`${API_BASE_URL}/api/social/activities/social-counts`,
      { activity_ids: [activity._id] }, { signal: abort.signal, timeout: 10000, withCredentials: true,
        katafitEventIdentity: identity, katafitEventOwner: ownerId, katafitEventPosition: null }) : null)
      .then(response => {
        const value = response?.data?.[activity._id];
        if (active && backendService.authenticatedIdentity === identity && backendService.positionOwner === ownerId
          && Number.isInteger(value?.kudos_count) && value.kudos_count >= 0
          && Number.isInteger(value?.comment_count) && value.comment_count >= 0 && typeof value.has_kudos === 'boolean') setSummary({ key, value });
      }).catch(() => {});
    return () => { active = false; abort.abort(); };
  }, [key, activity._id, backendService, identity, ownerId, social]);
  return <ActivitySocialBar key={key} activityId={activity._id} social={social || (summary?.key === key ? summary.value : undefined)}
    backendService={backendService} isOwner={isOwner} activityStatus={activity.status} />;
}

export function audienceLabel(audiences) {
  return audiences.length ? audiences.map(a => AUDIENCE_LABELS[a]).join(', ') : 'Private';
}

function cameraApi(backendService) {
  return createOwnerCameraArchiveApi({ backendService, identity: backendService.authenticatedIdentity, ownerId: backendService.positionOwner });
}

// Compact lane tile with the same layout conventions as MiniActivityCard.
export function CameraVideoTile({ activity, onClick }) {
  const live = activity.data.video.state === 'live';
  return (
    <div className="mini-activity-card camera-video-tile" data-activity-id={activity._id} role="button" tabIndex={0}
      title={live ? 'Live video' : 'Video'} onClick={() => onClick(activity)} onKeyDown={e => { if (e.key === 'Enter') onClick(activity); }}>
      <span className="mini-activity-icon">🎥</span>
      {live && <span className="media-video-live-dot" aria-label="Live" />}
    </div>
  );
}

// Decoding a verified Blob is local; a browser that never produces a poster
// frame or an error must still leave Loading. Metadata alone is not readiness.
export const DECODE_DEADLINE_MS = 20000;
const idle = key => ({ key, status: 'idle', parts: [], part: null, url: null, duration: null, failure: null });
const FAILURES = { request: 'Video unavailable. It could not be loaded.', decode: 'Video unavailable. This browser could not play it.',
  stalled: 'Video unavailable. It did not finish loading.' };

// One exact video as a Media attachment of its parent; the parent card owns
// name/header/social. A ready recording loads its first part once when its tile
// is on screen: the same verified Blob is the poster source, the native
// duration and, when opened, the shared gallery's bytes (never a second download).
function CameraVideo({ activity, backendService, isOwner = false, onWatchLive, onPlay, onOpen, onDeleted, removed = null, timeZone }) {
  const video = activity.data.video;
  const streams = useContext(SharedCameraStreamsContext);
  const [player, setPlayer] = useState(() => idle(null));
  const [confirming, setConfirming] = useState(false);
  const [deletion, setDeletion] = useState(null);
  // Playback/deletion ownership: account + parent + exact video + lifecycle
  // state. Any change (or unmount) aborts requests, revokes the URL and fences
  // late results; state of another key is never shown.
  const ownerKey = `${backendService?.authenticatedIdentity}|${backendService?.positionOwner}|${activity._id}|${video.ref.id}|${video.state}`;
  const session = useRef(null);
  useEffect(() => {
    const mine = { key: ownerKey, abort: new AbortController(), url: null };
    session.current = mine;
    return () => {
      mine.abort.abort();
      if (mine.url) URL.revokeObjectURL(mine.url);
      mine.url = null;
      if (session.current === mine) session.current = null;
    };
  }, [ownerKey]);
  const [shownKey, setShownKey] = useState(ownerKey);
  if (shownKey !== ownerKey) { setShownKey(ownerKey); setDeletion(null); setConfirming(false); }
  const view = player.key === ownerKey ? player : idle(ownerKey);

  const owns = mine => session.current === mine && !mine.abort.signal.aborted;
  const release = mine => { if (mine.url) { URL.revokeObjectURL(mine.url); mine.url = null; } mine.blob = null; };
  // One load per owner at a time (synchronous admission); a newer generation
  // supersedes an older one, and any displaced URL is revoked on publication.
  const load = async () => {
    const mine = session.current;
    if (!mine || mine.loading) return;
    const generation = (mine.generation || 0) + 1;
    mine.generation = generation;
    mine.loading = true;
    const current = () => owns(mine) && mine.generation === generation;
    release(mine);
    setPlayer({ ...idle(mine.key), status: 'loading' });
    let parts = [];
    try {
      const api = cameraApi(backendService);
      parts = await api.parts(video.ref.id, mine.abort.signal);
      if (!current()) return;
      if (!parts.length) { setPlayer({ ...idle(mine.key), status: 'error', failure: 'request' }); return; }
      const blob = await api.media(video.ref.id, parts[0], mine.abort.signal);
      if (!current()) return;
      release(mine);
      mine.blob = blob;
      mine.url = URL.createObjectURL(blob);
      setPlayer({ ...idle(mine.key), status: 'decoding', parts, part: parts[0].name, url: mine.url });
    } catch { if (current()) setPlayer({ ...idle(mine.key), status: 'error', parts, failure: 'request' }); }
    finally { if (mine.generation === generation) mine.loading = false; }
  };
  const loadRef = useRef(load); loadRef.current = load;
  // Decoder metadata only supplies the duration; an accepted poster frame is readiness.
  const durationOf = url => duration => setPlayer(prev => (prev.url !== url || prev.duration !== null || !(Number.isFinite(duration) && duration > 0)) ? prev
    : { ...prev, duration });
  const previewReady = url => () => setPlayer(prev => (prev.url === url && prev.status === 'decoding' ? { ...prev, status: 'ready' } : prev));
  const undecodable = (url, failure = 'decode') => () => {
    const mine = session.current;
    if (mine?.url !== url) return;
    release(mine);
    setPlayer(prev => prev.url !== url ? prev : { ...prev, status: 'error', url: null, failure });
  };
  const undecodableRef = useRef(undecodable); undecodableRef.current = undecodable;

  // Ordinary attachments preview themselves; a camera tile does so only once on
  // screen, so a long feed never downloads every day's recordings at once.
  const tileRef = useRef(null);
  const preview = video.state === 'ready' && !onPlay && Boolean(backendService?.client) && typeof IntersectionObserver === 'function';
  const waiting = view.status === 'idle' && deletion === null;
  useEffect(() => {
    if (!preview || !waiting || !tileRef.current) return undefined;
    const observer = new IntersectionObserver(entries => {
      if (!entries.some(entry => entry.isIntersecting)) return;
      observer.disconnect();
      loadRef.current();
    }, { rootMargin: '200px' });
    observer.observe(tileRef.current);
    return () => observer.disconnect();
  }, [preview, waiting, ownerKey]);
  useEffect(() => {
    if (view.status !== 'decoding') return undefined;
    const timer = setTimeout(() => undecodableRef.current(view.url, 'stalled')(), DECODE_DEADLINE_MS);
    return () => clearTimeout(timer);
  }, [view.status, view.url]);

  // Opens the shared gallery; a verified preview Blob is handed over so its
  // row needs no second download. Without one, the gallery fetches fresh.
  const play = () => {
    if (onPlay) return onPlay(activity);
    const mine = session.current;
    onOpen?.(video.ref.id, mine?.blob && view.url ? { blob: mine.blob, parts: view.parts, part: view.part } : null);
    return undefined;
  };
  // Deleted from the gallery: the same accepted tombstone outcome as here.
  useEffect(() => {
    if (!removed) return;
    const mine = session.current;
    if (mine) { mine.abort.abort(); release(mine); }
    setPlayer(idle(ownerKey));
    setConfirming(false);
    setDeletion(removed);
  }, [removed]); // eslint-disable-line react-hooks/exhaustive-deps
  const watchLive = () => {
    if (onWatchLive) return onWatchLive(activity);
    // Opens the existing authorized live viewer on this exact session.
    if (streams?.watch) streams.watch(video.ref.id); else { streams?.open?.(); streams?.tab?.('live'); }
    return undefined;
  };
  const remove = async () => {
    const mine = session.current;
    if (!mine) return;
    setConfirming(false); setDeletion('sending');
    try {
      const outcome = await cameraApi(backendService).remove(video.ref.id, mine.abort.signal);
      if (!owns(mine)) return;
      if (outcome === 'complete' || outcome === 'pending') {
        mine.abort.abort();
        release(mine);
        setPlayer(idle(mine.key));
      }
      setDeletion(outcome);
      onDeleted?.(activity, outcome);
    } catch { if (owns(mine)) setDeletion('unknown'); }
  };

  if (deletion === 'complete' || deletion === 'pending') {
    return <div className="media-file-entry media-video-entry" data-video-id={video.ref.id}><p role="status">{DELETION[deletion]}</p></div>;
  }
  const time = value => <time dateTime={value}>{new Intl.DateTimeFormat(undefined, { timeZone, timeStyle: 'short' }).format(new Date(value))}</time>;
  // Server lifecycle times (observed live → ended), labelled as the broadcast.
  const start = video.live_since, end = video.state === 'live' ? null : video.ended_at;
  const tile = (() => {
    if (view.url) {
      return <div className="media-file" role="button" tabIndex={0} aria-label="Play video" onClick={play} onKeyDown={e => { if (e.key === 'Enter') play(); }}>
        <VideoThumbnail key={view.url} src={view.url} alt={`${video.device_name || 'Video'} preview`} onDuration={durationOf(view.url)} onPreviewReady={previewReady(view.url)} onError={undecodable(view.url)} />
        {view.duration !== null && <span className="media-video-duration">{formatMediaDuration(view.duration)}</span>}
      </div>;
    }
    return <div className="media-file media-video-placeholder" ref={tileRef}>
      <VideoCameraIcon className="video-icon" aria-hidden="true" />
      {video.state === 'live' && <button type="button" onClick={watchLive}>Watch live</button>}
      {video.state === 'starting' && <p role="status">Starting broadcast…</p>}
      {video.state === 'processing' && <p role="status">Processing video…</p>}
      {video.state === 'ready' && view.status === 'idle' && <button type="button" onClick={play}>Play video</button>}
      {view.status === 'loading' && <p role="status">Loading video…</p>}
      {video.state === 'unavailable' && <p>{UNAVAILABLE[video.reason]}</p>}
    </div>;
  })();
  return (
    <div className="media-file-entry media-video-entry" data-video-id={video.ref.id}>
      {tile}
      <p className="media-video-entry-header">{video.device_name && <span className="media-video-device">{video.device_name}</span>}
        {(start || end) && <span className="media-video-broadcast">{start ? 'Broadcast ' : 'Broadcast end '}{start && time(start)}{start && end && '–'}{end && time(end)}</span>}
        {video.state === 'live' && <span className="media-video-entry-live">Live</span>}</p>
      {view.parts.length > 1 && <p className="media-video-parts">{view.parts.length} parts</p>}
      {view.status === 'error' && <p role="alert">{FAILURES[view.failure]} <button type="button" onClick={() => load()}>Retry</button></p>}
      {isOwner && <div className="media-video-card-actions">
        {!confirming && <button type="button" className="camera-danger-btn" disabled={deletion === 'sending'} onClick={() => setConfirming(true)}>{deletion === 'unknown' ? 'Retry delete' : 'Delete video'}</button>}
        {confirming && <><button type="button" className="camera-danger-btn" onClick={remove}>Confirm delete</button><button type="button" onClick={() => setConfirming(false)}>Keep video</button></>}
        {deletion && <p role="status">{DELETION[deletion]}</p>}
      </div>}
    </div>
  );
}

// The canonical parent card: one header/social bar, and every exact video (one
// per device recording) as an attachment in the ordinary Media gallery. Single
// and multi-video parents share this layout, so a second device joining never
// remounts (and so never interrupts) the first device's video.
export default function CameraVideoMedia(props) {
  // embedded: inside the ordinary activity shell, which already shows the name.
  const { activity, authorName, backendService, embedded = false, isOwner = false, social, timeZone } = props;
  const videos = Array.isArray(activity.data.videos) && activity.data.videos.length ? activity.data.videos : [activity.data.video];
  const hostModal = useModal();
  const modal = props.modalService || hostModal;
  const [removed, setRemoved] = useState({});
  // The same modal host and gallery stage as uploaded attachments, over this
  // parent's ready videos (siblings) with typed camera operations.
  const openGallery = (startId, start) => {
    if (!modal?.open || !backendService) return;
    modal.open(CameraVideoGallery, {
      activity, videos: videos.filter(v => v.state === 'ready' && !removed[v.ref.id]), startId, start, backendService,
      identity: backendService.authenticatedIdentity, ownerId: backendService.positionOwner, isOwner, timeZone,
      onDeleted: (sid, outcome) => setRemoved(prev => ({ ...prev, [sid]: outcome })),
    });
  };
  const name = activity._id !== activity.data.video.ref.id || Object.prototype.hasOwnProperty.call(activity, 'due_at') ? activity.name : null;
  // A linked parent shows its capture start; a later completion never relabels it.
  const date = Object.prototype.hasOwnProperty.call(activity, 'due_at') ? activity.created_at : activity.completed_at || activity.created_at;
  const when = new Intl.DateTimeFormat(undefined, { timeZone, dateStyle: 'medium', timeStyle: 'short' }).format(new Date(date));
  return (
    <article className={`media-video-card${videos.length > 1 ? ' media-video-group' : ''}`} data-activity-id={activity._id} aria-label={`${name || 'Video'} by ${authorName || 'member'}`}>
      {name && !embedded && <h3 className="media-video-card-name">{name}</h3>}
      <header className="media-video-card-header">
        <strong>{authorName || 'Member'}</strong>
        <time dateTime={date}>{when}</time>
        <span className="media-video-card-audience">{audienceLabel(activity.audiences)}</span>
        {videos.some(v => v.state === 'live') && <span className="media-video-live-badge" role="status">Live</span>}
      </header>
      <div className="media-files media-video-card-body">
        {videos.map(video => <CameraVideo key={video.ref.id} {...props} activity={{ ...activity, data: { video } }} onOpen={openGallery} removed={removed[video.ref.id] || null} />)}
      </div>
      {backendService && <CameraSocialBar activity={activity} social={social} backendService={backendService} isOwner={isOwner} />}
    </article>
  );
}
